import memoizeOne from 'memoize-one';
import isNotBoolean from './isNotBoolean';
import {
	FlattenedQuestionnaire,
	Field,
	ObjectField,
	MixedObjectField,
	GetObjectField,
	GetMixedObjectField,
	ObjectFieldWithStructure,
	GetObjectFieldWithStructure,
	stringObject,
	stringAndDateObject
} from '../questionnaireHelpers';
import dateStringToObject from './dateStringToObject';

const getQuestionnaireGetters = memoizeOne(buildQuestionnaireGetters);

function buildQuestionnaireGetters(questionnaire: FlattenedQuestionnaire) {
	function getValueByKey(key: string, nullValue: any = ''): string {
		const field = questionnaire.find(findField => findField.key === key);
		if (field?.value && Array.isArray(field.value)) {
			return field.value.length === 0 ||
				(field.isFalseValid === false && field.value[0] === 'false')
				? ''
				: field.value[0];
		}

		return field ? field.value : nullValue;
	}

	function getNotBooleanValueByKey(key: string, nullValue: any = ''): string {
		const field = questionnaire.find(findField => findField.key === key);
		if (field?.value && Array.isArray(field.value)) {
			return field.value.length === 0
				? ''
				: isNotBoolean(field.value[0])
				? field.value[0]
				: nullValue;
		}

		return field && isNotBoolean(field.value) ? field.value : nullValue;
	}

	function getNotBooleanValueFromArrayByKey(key: string, nullValue: any = ''): string {
		const value = getValueByKey(key);
		return isNotBoolean(value) ? value : nullValue;
	}

	function getBooleanOrStringValueFromArrayByKey(key: string): string | boolean {
		const value = getValueByKey(key);
		return value === 'true' ? true : value;
	}

	function getBooleanValueByKey(key: string): boolean | null {
		let value = getValueByKey(key, null);
		if (Array.isArray(value)) [value] = value;
		if (value === 'true') return true;
		if (value === 'false') return false;
		return value === null ? null : !!value;
	}

	function getFieldsByKey(key: string): Field[] {
		return questionnaire.filter(field => field.key === key);
	}

	function getObjectsByKeys(fields: GetObjectField, skipBoolean = false): ObjectField[] {
		// Pre-fill first value with shape of the object
		const result: ObjectField[] = [{ ...fields }];
		Object.keys(fields).forEach(field => {
			result[0][field] = '';
		});
		// Fill result with values
		Object.entries(fields).forEach(([fieldName, key]) => {
			getFieldsByKey(key).forEach(field => {
				if (typeof field.index !== 'undefined') {
					result[field.index] = result[field.index] || {};
					if (
						(skipBoolean && typeof field.value === 'boolean') ||
						(Array.isArray(field.value) && field.value.length === 0)
					) {
						result[field.index][fieldName] = '';
					} else if (Array.isArray(field.value) && field.value.length === 1) {
						[result[field.index][fieldName]] = field.value;
					} else {
						result[field.index][fieldName] = field.value;
					}
				}
			});
		});

		return result;
	}

	const preFillResultElement = (
		result: ObjectFieldWithStructure[],
		fields: GetObjectFieldWithStructure,
		fieldIndex: number
	) => {
		const modifiedResult = result;
		Object.entries(fields).forEach(([fieldName, key]) => {
			if (typeof key === 'string') {
				modifiedResult[fieldIndex][fieldName] = '';
			} else if (Array.isArray(key)) {
				Object.entries(key[0]).forEach(([innerFieldName]) => {
					modifiedResult[fieldIndex][fieldName][0][innerFieldName] = '';
				});
			} else {
				Object.entries(key).forEach(([innerFieldName]) => {
					modifiedResult[fieldIndex][fieldName][innerFieldName] = '';
				});
			}
		});

		return modifiedResult;
	};

	const addEmptyResultElement = (
		result: ObjectFieldWithStructure[],
		fields: GetObjectFieldWithStructure,
		fieldIndex: number
	) => {
		let modifiedResult = result;
		if (modifiedResult.length === fieldIndex) {
			modifiedResult.push({ ...structuredClone(fields) });
			modifiedResult = preFillResultElement(modifiedResult, fields, fieldIndex);
		}

		return modifiedResult;
	};

	const getObjectsWithStructuresByKeys = (
		fields: GetObjectFieldWithStructure,
		skipBoolean = false
	): ObjectFieldWithStructure[] => {
		// Pre-fill first value with shape of the object
		let result: ObjectFieldWithStructure[] = [{ ...structuredClone(fields) }];
		result = preFillResultElement(result, fields, 0);

		// Fill result with values
		Object.entries(fields).forEach(singleField => {
			const [fieldName, key] = singleField;
			if (typeof key === 'string') {
				getFieldsByKey(key).forEach(field => {
					if (typeof field.index !== 'undefined') {
						result = addEmptyResultElement(result, fields, field.index);

						result[field.index] = result[field.index] || {};
						if (
							(skipBoolean && typeof field.value === 'boolean') ||
							(Array.isArray(field.value) && field.value.length === 0)
						) {
							result[field.index][fieldName] = '';
						} else if (Array.isArray(field.value) && field.value.length === 1) {
							[result[field.index][fieldName]] = field.value;
						} else {
							result[field.index][fieldName] = field.value;
						}
					}
				});
			} else if (Array.isArray(key)) {
				key.forEach(singleKey => {
					Object.entries(singleKey).forEach(singleFieldKey => {
						const [innerFieldName, innerKey] = singleFieldKey;

						let innerIndex = 0;
						let innerSubIndex = 0;
						getFieldsByKey(innerKey).forEach(innerField => {
							if (innerField?.index && innerIndex < innerField?.index) {
								innerSubIndex = 0;
							}
							const subIndex = innerField.subIndex || innerSubIndex;

							if (
								typeof innerField.index !== 'undefined' &&
								typeof subIndex !== 'undefined'
							) {
								result = addEmptyResultElement(result, fields, innerField.index);

								if (
									(result[innerField.index][fieldName] as ObjectField[])
										?.length === subIndex
								) {
									(result[innerField.index][fieldName] as ObjectField[]).push({
										...structuredClone(singleKey)
									});

									key.forEach(({ name }) => {
										if (innerField.index) {
											result[innerField.index][fieldName][name] = '';
										}
									});
								}

								result[innerField.index] = result[innerField.index] || {};
								if (
									(skipBoolean && typeof innerField.value === 'boolean') ||
									(Array.isArray(innerField.value) &&
										innerField.value.length === 0)
								) {
									result[innerField.index][fieldName][subIndex][innerFieldName] =
										'';
								} else if (
									Array.isArray(innerField.value) &&
									innerField.value.length === 1
								) {
									[
										result[innerField.index][fieldName][subIndex][
											innerFieldName
										]
									] = innerField.value;
								} else {
									result[innerField.index][fieldName][subIndex][innerFieldName] =
										innerField.value;
								}
							}
							if (innerField?.index) {
								innerIndex = innerField?.index;
							}
							innerSubIndex += 1;
						});
					});
				});
			} else {
				Object.entries(key).forEach(([innerFieldName, innerKey]) => {
					getFieldsByKey(innerKey).forEach(innerField => {
						if (typeof innerField.index !== 'undefined') {
							result = addEmptyResultElement(result, fields, innerField.index);

							if (
								innerField.index > 0 &&
								typeof result[innerField.index][fieldName] === 'undefined'
							) {
								result[innerField.index][fieldName] = {};
							}
							result[innerField.index] = result[innerField.index] || {};
							if (
								(skipBoolean && typeof innerField.value === 'boolean') ||
								(Array.isArray(innerField.value) && innerField.value.length === 0)
							) {
								result[innerField.index][fieldName][innerFieldName] = '';
							} else if (
								Array.isArray(innerField.value) &&
								innerField.value.length === 1
							) {
								[
									result[innerField.index][fieldName][innerFieldName]
								] = innerField.value;
							} else {
								result[innerField.index][fieldName][innerFieldName] =
									innerField.value;
							}
						}
					});
				});
			}
		});

		return result;
	};

	// all items in object that start with prefix address will be added to sub-object address: { addressStreetName: ${value} };
	// similarly with placeOfBirth: { placeOfBirthCity: ''... }
	function getValuesByType(mixedObjectFields: GetMixedObjectField): MixedObjectField[] {
		const result: MixedObjectField[] = [{}];
		const addressArray: stringObject[] = [];
		const placeOfBirthArray: stringObject[] = [];
		const isinArray: stringObject[] = [];
		const residenceArray: stringObject[] = [];
		const headNameArray: stringObject[] = [];
		const headInformationArray: stringAndDateObject[] = [];
		const headAddressArray: stringObject[] = [];
		const headActualAddressArray: stringObject[] = [];
		const fullNameArray: stringObject[] = [];
		const typeOfControlArray: stringObject[] = [];
		const documentInfoArray: stringObject[] = [];
		const contactInformationArray: stringObject[] = [];
		const countryOfResidenceForTaxArray: stringObject[] = [];
		const personalInformationArray: stringAndDateObject[] = [];
		const actualAddressArray: stringObject[] = [];
		const legalFormArray: stringObject[] = [];
		// Fill result with values
		Object.entries(mixedObjectFields).forEach(
			([fieldName, { type, key, removeDoubleSpaces }]) => {
				getFieldsByKey(key).forEach(field => {
					if (typeof field.index !== 'undefined') {
						result[field.index] = result[field.index] || {};

						if (field.value === false && field.isFalseValid === false)
							result[field.index][fieldName] = '';
						else if (type === 3) {
							if (
								typeof field.value === 'string' ||
								(Array.isArray(field.value) && typeof field.value[0] === 'string')
							) {
								result[field.index][fieldName] = dateStringToObject(
									Array.isArray(field.value) ? field.value[0] : field.value
								);
							} else
								result[field.index][fieldName] = Array.isArray(field.value)
									? field.value[0]
									: field.value;
						} else if (
							(typeof field.value === 'string' ||
								typeof field.value === 'number' ||
								Array.isArray(field.value)) &&
							type > 3
						) {
							switch (type) {
								case 4:
									// store address values for later
									while (addressArray.length <= field.index) {
										addressArray.push({
											isActualAddressEnabled: '',
											addressCountry: '',
											addressStreetName: '',
											addressBuildingNumber: '',
											addressApartmentNumber: '',
											addressCity: '',
											addressPostCode: ''
										});
									}
									addressArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 5:
									// store place of birth values for later
									while (placeOfBirthArray.length <= field.index) {
										placeOfBirthArray.push({
											placeOfBirthCity: '',
											placeOfBirthCountry: ''
										});
									}
									placeOfBirthArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 6:
									// store ISIN values for later
									while (isinArray.length <= field.index) {
										isinArray.push({
											isinIsEnabled: '',
											isinISIN: ''
										});
									}
									isinArray[field.index][fieldName] = Array.isArray(field.value)
										? field.value[0]
										: field.value;
									break;
								case 7:
									// store countryOfResidence values for later
									while (residenceArray.length <= field.index) {
										residenceArray.push({
											country: '',
											tin: ''
										});
									}
									residenceArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 8:
									// store name values for later
									while (headNameArray.length <= field.index) {
										headNameArray.push({
											firstName: '',
											middleName: '',
											surname: '',
											lastName: ''
										});
									}
									headNameArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 9:
									while (headInformationArray.length <= field.index) {
										headInformationArray.push({
											citizenship: '',
											personalCode: '',
											dateOfBirth: { day: '', month: '', year: '' },
											phoneNumber: '',
											email: ''
										});
									}
									if (fieldName === 'dateOfBirth')
										headInformationArray[field.index][
											fieldName
										] = dateStringToObject(
											Array.isArray(field.value)
												? field.value[0]
												: field.value
										);
									else
										headInformationArray[field.index][
											fieldName
										] = Array.isArray(field.value)
											? field.value[0]
											: field.value;
									break;
								case 10:
									while (headAddressArray.length <= field.index) {
										headAddressArray.push({
											country: '',
											streetName: '',
											buildingNumber: '',
											apartmentNumber: '',
											city: '',
											postCode: ''
										});
									}
									headAddressArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 11:
									while (fullNameArray.length <= field.index) {
										fullNameArray.push({
											name: '',
											lastName: ''
										});
									}
									fullNameArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 12:
									while (typeOfControlArray.length <= field.index) {
										typeOfControlArray.push({
											type: '',
											specify: ''
										});
									}
									typeOfControlArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 13:
									while (documentInfoArray.length <= field.index) {
										documentInfoArray.push({
											documentType: '',
											documentNumber: ''
										});
									}
									documentInfoArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 14:
									while (contactInformationArray.length <= field.index) {
										contactInformationArray.push({
											phoneNumber: '',
											email: ''
										});
									}
									contactInformationArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 15:
									while (countryOfResidenceForTaxArray.length <= field.index) {
										countryOfResidenceForTaxArray.push({
											country: '',
											countryOfResidence: '',
											tin: ''
										});
									}
									if (
										fieldName === 'countryOfResidence' &&
										countryOfResidenceForTaxArray[field.index].country
									)
										countryOfResidenceForTaxArray[
											field.index
										].country = Array.isArray(field.value)
											? field.value[0]
											: field.value;
									else
										countryOfResidenceForTaxArray[field.index][
											fieldName
										] = Array.isArray(field.value)
											? field.value[0]
											: field.value;
									break;
								case 16:
									while (personalInformationArray.length <= field.index) {
										personalInformationArray.push({
											citizenship: '',
											personalCode: '',
											dateOfBirth: { day: '', month: '', year: '' },
											placeOfBirth: ''
										});
									}
									if (fieldName === 'dateOfBirth')
										personalInformationArray[field.index][
											fieldName
										] = dateStringToObject(
											Array.isArray(field.value)
												? field.value[0]
												: field.value
										);
									else
										personalInformationArray[field.index][
											fieldName
										] = Array.isArray(field.value)
											? field.value[0]
											: field.value;
									break;
								case 17:
									while (actualAddressArray.length <= field.index) {
										actualAddressArray.push({
											isActualAddressEnabled: '',
											country: '',
											streetName: '',
											buildingNumber: '',
											apartmentNumber: '',
											city: '',
											postCode: ''
										});
									}
									actualAddressArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 18:
									while (legalFormArray.length <= field.index) {
										legalFormArray.push({
											type: '',
											limitedLiabilityCompany: '',
											other: ''
										});
									}
									legalFormArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								case 19:
									while (headActualAddressArray.length <= field.index) {
										headActualAddressArray.push({
											isActualAddressDifferent: '',
											actualAddressCountry: '',
											actualAddressStreetName: '',
											actualAddressBuildingNumber: '',
											actualAddressApartmentOrOfficeNumber: '',
											actualAddressCity: '',
											actualAddressPostCode: ''
										});
									}
									headActualAddressArray[field.index][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
									break;
								default:
									break;
							}
						} else {
							const fieldValue = Array.isArray(field.value)
								? field.value[0]
								: field.value;

							if (removeDoubleSpaces && typeof fieldValue === 'string') {
								result[field.index][fieldName] = fieldValue.replaceAll('  ', ' ');
							} else {
								result[field.index][fieldName] = fieldValue;
							}
						}
					} else if (typeof field.value === 'string' && type > 7) {
						result[0] = result[0] || {};
						switch (type) {
							case 8:
								// store name values for later
								if (headNameArray.length === 0) {
									headNameArray.push({
										firstName: '',
										middleName: '',
										surname: '',
										lastName: ''
									});
								}
								headNameArray[0][fieldName] = field.value;
								break;
							case 9:
								while (headInformationArray.length === 0) {
									headInformationArray.push({
										citizenship: '',
										personalCode: '',
										dateOfBirth: { day: '', month: '', year: '' },
										phoneNumber: '',
										email: ''
									});
								}
								if (fieldName === 'dateOfBirth')
									headInformationArray[0][fieldName] = dateStringToObject(
										field.value
									);
								else headInformationArray[0][fieldName] = field.value;
								break;
							case 10:
								if (headAddressArray.length === 0) {
									headAddressArray.push({
										country: '',
										streetName: '',
										buildingNumber: '',
										apartmentNumber: '',
										city: '',
										postCode: ''
									});
								}
								headAddressArray[0][fieldName] = field.value;
								break;
							case 14:
								while (contactInformationArray.length === 0) {
									contactInformationArray.push({
										phoneNumber: '',
										email: ''
									});
								}
								contactInformationArray[0][fieldName] = field.value;
								break;
							case 15:
								while (countryOfResidenceForTaxArray.length === 0) {
									countryOfResidenceForTaxArray.push({
										country: '',
										countryOfResidence: '',
										tin: ''
									});
								}
								if (fieldName === 'countryOfResidence')
									countryOfResidenceForTaxArray[0].country = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
								else
									countryOfResidenceForTaxArray[0][fieldName] = Array.isArray(
										field.value
									)
										? field.value[0]
										: field.value;
								break;
							case 16:
								while (personalInformationArray.length === 0) {
									personalInformationArray.push({
										citizenship: '',
										personalCode: '',
										dateOfBirth: { day: '', month: '', year: '' },
										placeOfBirth: ''
									});
								}
								if (fieldName === 'dateOfBirth')
									personalInformationArray[0][fieldName] = dateStringToObject(
										field.value
									);
								else personalInformationArray[0][fieldName] = field.value;
								break;
							case 17:
								while (actualAddressArray.length === 0) {
									actualAddressArray.push({
										isActualAddressEnabled: '',
										country: '',
										streetName: '',
										buildingNumber: '',
										apartmentNumber: '',
										city: '',
										postCode: ''
									});
								}
								actualAddressArray[0][fieldName] = field.value;
								break;
							case 18:
								while (legalFormArray.length === 0) {
									legalFormArray.push({
										type: '',
										limitedLiabilityCompany: '',
										other: ''
									});
								}
								legalFormArray[0][fieldName] = field.value;
								break;
							case 19:
								if (headActualAddressArray.length === 0) {
									headActualAddressArray.push({
										isActualAddressDifferent: '',
										actualAddressCountry: '',
										actualAddressStreetName: '',
										actualAddressBuildingNumber: '',
										actualAddressApartmentOrOfficeNumber: '',
										actualAddressCity: '',
										actualAddressPostCode: ''
									});
								}
								headActualAddressArray[0][fieldName] = field.value;
								break;
							default:
								break;
						}
					}
				});
			}
		);

		// returned merged object
		return result.map((item, index) => {
			const resultItem: any = {};
			if (addressArray[index]) resultItem.address = addressArray[index];
			if (placeOfBirthArray[index]) resultItem.placeOfBirth = placeOfBirthArray[index];
			if (isinArray[index]) resultItem.isin = isinArray[index];
			if (residenceArray[index]) resultItem.residence = residenceArray[index];
			if (headNameArray[index]) resultItem.name = headNameArray[index];
			if (headInformationArray[index]) resultItem.information = headInformationArray[index];
			if (headAddressArray[index]) resultItem.address = headAddressArray[index];
			if (fullNameArray[index]) resultItem.fullName = fullNameArray[index];
			if (typeOfControlArray[index]) resultItem.typeOfControl = typeOfControlArray[index];
			if (documentInfoArray[index]) resultItem.documentInfo = documentInfoArray[index];
			if (contactInformationArray[index])
				resultItem.contactInformation = contactInformationArray[index];
			if (countryOfResidenceForTaxArray[index])
				resultItem.countryOfResidenceForTax = countryOfResidenceForTaxArray[index];
			if (personalInformationArray[index])
				resultItem.personalInformation = personalInformationArray[index];
			if (actualAddressArray[index]) resultItem.actualAddress = actualAddressArray[index];
			if (legalFormArray[index]) resultItem.legalForm = legalFormArray[index];
			if (headActualAddressArray[index])
				resultItem.actualAddress = headActualAddressArray[index];
			return {
				...item,
				...resultItem
			};
		});
	}

	function getArrayValueByKey(key: string): string[] {
		const fields = questionnaire.filter(findField => findField.key === key);
		if (fields.length && fields[0].value) {
			const returns = fields.map((field: Field) => {
				if (Array.isArray(field.value)) return field.value[0] || '';
				return field.value as string;
			});
			return returns;
		}

		return [];
	}

	const getArrayByKey = (key: string) => {
		const field = questionnaire.find(findField => findField.key === key);
		if (field?.value && Array.isArray(field.value) && field.value?.length) {
			return field.value?.filter(Boolean);
		}

		return [];
	};

	return {
		getValueByKey,
		getBooleanValueByKey,
		getObjectsByKeys,
		getObjectsWithStructuresByKeys,
		getArrayValueByKey,
		getArrayByKey,
		getValuesByType,
		getNotBooleanValueByKey,
		getNotBooleanValueFromArrayByKey,
		getBooleanOrStringValueFromArrayByKey
	};
}

export default getQuestionnaireGetters;
